import { useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  SweepWalletItem,
  collectSweep,
  listSweepHistory,
} from '../../../services/xbitRelaySweepService';

type Props = {
  record: SweepWalletItem;
  onClose(): void;
};

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 5;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const newIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export function CollectDialog({ record, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  // One idempotency key per opened dialog (one Collect click).
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const pollFinalState = async (attempt = 0): Promise<void> => {
    if (attempt >= POLL_ATTEMPTS) {
      notify('module.xbitRelaySweepMonitor.collectPending', { type: 'info' });
      return;
    }
    await wait(POLL_INTERVAL_MS);
    try {
      const res = await listSweepHistory({
        address: record.address,
        chainId: record.chainId,
        pageSize: 1,
      });
      const fresh = res.data?.items?.[0];
      const status = fresh?.status;
      if (status === 'SUCCESS' || status === 'SKIPPED') {
        notify('module.xbitRelaySweepMonitor.collectSuccess', {
          type: 'success',
          messageArgs: { status },
        });
        return;
      }
      if (status === 'FAILED') {
        notify(fresh?.errorMsg || t('common.error'), { type: 'error' });
        return;
      }
    } catch {
      // ignore poll errors, retry next tick
    }
    await pollFinalState(attempt + 1);
  };

  const handleCollect = async () => {
    setLoading(true);
    try {
      const { data } = await collectSweep({
        address: record.address,
        chainId: record.chainId,
        token: record.token,
        idempotencyKey,
      });

      if (data.duplicate) {
        notify('module.xbitRelaySweepMonitor.collectDuplicate', {
          type: 'warning',
        });
        onClose();
        return;
      }

      if (!data.success) {
        notify(data.message || t('module.xbitRelaySweepMonitor.collectRejected'), {
          type: 'error',
        });
        return;
      }

      notify('module.xbitRelaySweepMonitor.collectQueued', { type: 'info' });
      onClose();
      await pollFinalState();
      refresh();
    } catch (err: any) {
      notify(
        err?.response?.data?.message || err?.message || t('common.error'),
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelaySweepMonitor.collectTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {t('module.xbitRelaySweepMonitor.collectConfirm')}
          </Typography>

          <Stack spacing={0.5}>
            <Typography variant="body2">
              <b>{t('module.xbitRelaySweepMonitor.chain')}:</b> {record.chainId}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelaySweepMonitor.token')}:</b>{' '}
              {record.symbol || record.token}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelaySweepMonitor.balance')}:</b> {record.balance}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              <b>{t('module.xbitRelaySweepMonitor.address')}:</b> {record.address}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCollect}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelaySweepMonitor.collect')}
        </Button>
      </DialogActions>
    </>
  );
}
